import { type RootState } from '@/store';
import { type ChargePoint, type Connector } from '@/types/swagger';
import _ from 'lodash';
import { createSelector } from 'reselect';

// Common
export const getMessage = (state: RootState) => state.common.message;
export const getSnackbarMessage = (state: RootState) => state.common.snackbarMessage;
export const isOpen = (state: RootState) => state.common.open;
export const drawerIsOpen = (state: RootState) => state.common.drawerOpen;
export const quickNotificationsOpen = (state: RootState) => state.common.notificationsOpen;

// Settings
export const getWidth = (state: RootState) => state.settings.width;
export const getLocale = (state: RootState) => state.settings.locale;
export const getLogo = (state: RootState) => state.settings.theme.logo;
export const getTheme = (state: RootState) => state.settings.theme;

// Widgets
export const getSelectedTab = (state: RootState) => state.widgets.selectedTab;
export const getEditMode = (state: RootState) => state.widgets.editMode;

// Auth
export const isLogging = (state: RootState) => state.auth.logging;
export const isFetchingUser = (state: RootState) => state.auth.fetching;
export const resetRequestSent = (state: RootState) => state.auth.resetRequestSent;
export const tokenIsValid = (state: RootState) => state.auth.tokenIsValid;
export const passwordConfirmed = (state: RootState) => state.auth.passwordConfirmed;

// Charging Profiles
export const getChargingProfilesIds = (state: RootState) => state.chargingProfiles.allIds;
export const getChargingProfileById = (state: RootState, id: number) =>
  state.chargingProfiles?.byId[id];
export const getAllChargingProfilesById = (state: RootState) => state.chargingProfiles?.byId;
export const getSyncStatusesById = (state: RootState) => state.chargingProfiles.syncStatusesById;
export const getResyncStatusesById = (state: RootState) =>
  state.chargingProfiles.resyncStatusesById;
export const chargingProfileIsUpdating = (state: RootState) => state.chargingProfiles.updating;
export const getGridConnectionChargingProfile = (state: RootState, gridConnectionId: number) =>
  state.chargingProfiles.byGridConnectionId[gridConnectionId];
export const getGridConnectionChargingProfiles = (state: RootState) =>
  state.chargingProfiles.byGridConnectionId;

// Tracker Logs
export const getAllTrackerLogsGroupedByVehicle = (state: RootState) =>
  state.trackerLogs.byVehicleNumberPlate;
export const lastTrackerLogIsFetching = (state: RootState) =>
  state.trackerLogs.lastTrackerLogIsFetching;
export const trackerLogsIsFetching = (state: RootState) => state.trackerLogs.trackerLogsIsFetching;

// Vehicles
export const pureGetVehicleById = (state: RootState, id: number) => state.vehicles?.byId[id];
export const pureGetAllVehiclesById = (state: RootState) => state.vehicles?.byId;
export const getSelectedVehicleId = (state: RootState) => state.vehicles.selectedVehicleId;
export const vehicleIsFetching = (state: RootState) => state.vehicles.fetching;

// Composed selectors
const mergeEntities = (parent: any, children: any, entries: any, idAttributes: any) => {
  if (parent) {
    const newParent = _.cloneDeep(parent);
    _.forEach(entries, (entrie, index) => {
      const idAttribute = idAttributes.length ? idAttributes[index][1] : 'id';
      if (typeof newParent[entrie[1]] === 'object') {
        const matchChildren = _.pickBy(children[index], (childrenItem) =>
          _.includes(newParent[entrie[1]], childrenItem[idAttribute])
        );
        newParent[entrie[0]] = _.map(matchChildren, (value) => value);
      } else {
        const matchChildren = _.find(
          children[index],
          (child) => child[idAttribute] === newParent[entrie[1]]
        );
        if (matchChildren) {
          newParent[entrie[0]] = [matchChildren];
        }
      }
    });
    return newParent;
  }
  return parent;
};

export const getAllById = (
  parentSelector: any,
  childrenSelectors: any,
  keysObject: any,
  idAttributesObject = {}
) =>
  createSelector(
    [parentSelector, ..._.map(childrenSelectors, (value) => value)],
    (parents, ...args) =>
      // @ts-expect-error unknown
      _.map(parents, (parent) =>
        mergeEntities(parent, args, _.entries(keysObject), _.entries(idAttributesObject))
      )
  );
export const getById = (
  parentSelector: any,
  childrenSelectors: any,
  keysObject: any,
  idAttributesObject = {}
) =>
  createSelector(
    [parentSelector, ..._.map(childrenSelectors, (value, key) => value)],
    (parent, ...args) =>
      mergeEntities(parent, args, _.entries(keysObject), _.entries(idAttributesObject))
  );

// Transactions
export const pureGetTransactionById = (state: RootState, id: number) =>
  state.transactions?.byId?.[id];
export const pureGetAllTransactionsById = (state: RootState) => state.transactions?.byId;
export const getAllTransactionsById = getAllById(
  pureGetAllTransactionsById,
  { vehicles: pureGetAllVehiclesById },
  { vehicles: 'vehicle' },
  { vehicles: 'number_plate' }
);
export const getTransactionById = getById(
  pureGetTransactionById,
  { vehicles: pureGetAllVehiclesById },
  { vehicles: 'vehicle' },
  { vehicles: 'number_plate' }
);
export const getAllChargePointTransactions = (state: RootState) =>
  state.transactions.byChargePointId;
export const getChargePointTransactions = (state: RootState, chargePointId: number) =>
  state.transactions.byChargePointId[chargePointId];
export const getSelectedTransactionTimestamp = (state: RootState) =>
  state.transactions.selectedTransactionTimestamp;
export const getSampledValuesListByTransactionId = (state: RootState) =>
  state.transactions.sampledValuesByTransactionId;
export const selectSampledValuesByTransactionId = (state: RootState, transactionId: number) =>
  state.transactions.sampledValuesByTransactionId[transactionId];
export const transactionsIsFetching = (state: RootState) => state.transactions.fetching;

// Charging Profiles
export const pureGetChargingProfileById = (state: RootState, id: number) =>
  state.chargingProfiles?.byId?.[id];
export const pureGetAllChargingProfilesById = (state: RootState) => state.chargingProfiles?.byId;

// Connectors
export const pureGetConnectorById = (state: RootState, id: number) => state.connectors?.byId?.[id];
export const pureGetAllConnectorsById = (state: RootState) => state.connectors?.byId;
export const getAllConnectorsByGridConnectionId = (state: RootState) =>
  state.connectors.byGridConnectionId;
export const getAllPowerValuesByConnectorIdByTimestamp = (state: RootState) =>
  state.connectors.powerValuesByConnectorByTimestamp;
export const getGridConnectionConnectors = (state: RootState, id: number) =>
  state.connectors.byGridConnectionId?.[id];
export const getSelectedConnectorId = (state: RootState) => state.connectors.selectedConnectorId;
export const powerIsFetching = (state: RootState) => state.connectors.powerFetching;
export const isToggling = (state: RootState) => state.connectors.toggling;
export const getAllGridConnectionConnectors = getAllById(
  getGridConnectionConnectors,
  {
    transactions: getAllTransactionsById,
    charging_profiles: pureGetAllChargingProfilesById,
  },
  {
    transactions: 'transaction',
    charging_profiles: 'charging_profiles',
  },
  {
    transactions: 'connector',
    charging_profiles: 'id',
  }
);
export const getAllConnectorsById = getAllById(
  pureGetAllConnectorsById,
  {
    transactions: getAllTransactionsById,
    charging_profiles: pureGetAllChargingProfilesById,
  },
  {
    transactions: 'transaction',
    charging_profiles: 'charging_profiles',
  },
  {
    transactions: 'connector',
    charging_profiles: 'id',
  }
);
export const getConnectorById = getById(
  pureGetConnectorById,
  {
    transactions: getAllTransactionsById,
    charging_profiles: pureGetAllChargingProfilesById,
  },
  {
    transactions: 'transaction',
    charging_profiles: 'charging_profiles',
  },
  {
    transactions: 'connector',
    charging_profiles: 'id',
  }
);

// Charge Points
export const pureGetChargePointById = (state: RootState, id: number) =>
  state.chargePoints?.byId[id];
export const pureGetAllChargePointsById = (state: RootState) => state.chargePoints?.byId;

export const getAllChargePointsById = getAllById(
  pureGetAllChargePointsById,
  { connectors: getAllConnectorsById },
  { connectors: 'connectors' }
);
export const getChargePointById = getById(
  pureGetChargePointById,
  { connectors: getAllConnectorsById },
  { connectors: 'connectors' }
);
export const pureGetAllChargePointsByGridConnectionId = (state: RootState, id: number) =>
  state.chargePoints.byGridConnectionId[id];
export const getAllChargePointsByGridConnectionId = getAllById(
  pureGetAllChargePointsByGridConnectionId,
  { connectors: getAllConnectorsById },
  { connectors: 'connectors' }
);

export const getSelectedChargePointId = (state: RootState) =>
  state.chargePoints.selectedChargePointId;
export const getSelectedChargePoint = createSelector(
  [getSelectedChargePointId, getAllChargePointsById],
  (selectedChargePointId, chargePoints) =>
    _.find(chargePoints, (chargePoint: ChargePoint) => selectedChargePointId === chargePoint.id)
);

export const selectChargePointsWithConnectors = createSelector(
  [pureGetAllChargePointsByGridConnectionId, getAllGridConnectionConnectors],
  (chargePoints, connectors) => {
    const allChargePoints: ChargePoint[] = [];
    Object.values(chargePoints ?? {}).forEach((chargePoint) => {
      const chargePointConnectors = Array.from(connectors as Iterable<Connector>).filter(
        (connector) => chargePoint.id === connector.charge_point
      );
      allChargePoints.push({
        ...chargePoint,
        connectors: chargePointConnectors,
      });
    });
    return allChargePoints;
  }
);
